import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronDown, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import { FarmCreateModal } from '@/components/farm';

interface FarmSwitcherProps {
  variant: 'sidebar' | 'drawer';
  onSelect?: () => void;
}

export function FarmSwitcher({ variant, onSelect }: FarmSwitcherProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onIdRoute = Object.keys(useParams({ strict: false })).length > 0;
  const { role, isOperator } = useMembership();
  const { farmId, currentFarm, farms, setSelectedFarmId } = useCurrentFarm();
  const [open, setOpen] = useState(false);
  const [isCreateFarmOpen, setIsCreateFarmOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const drawer = variant === 'drawer';

  useEffect(() => {
    function handleClickOutside(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  // No-farm onboarding: an operator with zero farms has no switcher, so surface a
  // standalone create action (the primary CTA lives on the /overview empty state).
  const createOnly = farms.length === 0;
  const itemClass = drawer ? 'min-h-12 px-3 text-base' : 'px-3 py-2 text-sm';

  let control: ReactNode = null;
  if (createOnly) {
    if (isOperator) {
      control = (
        <button
          onClick={() => setIsCreateFarmOpen(true)}
          className="-mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Farm</span>
        </button>
      );
    }
  } else {
    control = (
      <div className="relative -mt-2" ref={ref}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className={cn(
            'w-full flex items-center justify-between gap-2 px-3 font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors',
            drawer ? 'min-h-12 text-base' : 'py-2 text-sm'
          )}
        >
          <span className="truncate">{currentFarm?.name || 'Select Farm'}</span>
          <ChevronDown
            className={cn('w-4 h-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
          />
        </button>

        {open && (farms.length > 1 || isOperator) && (
          <div
            className={cn(
              'mt-1 w-full bg-white rounded-lg border border-gray-200 py-1',
              !drawer && 'absolute z-50 shadow-lg'
            )}
          >
            {farms.map((farm) => (
              <button
                key={farm.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect?.();
                  // Issue #128: technicians live on /entry; routing them
                  // to /overview on every farm switch is disorienting.
                  // Owner/supervisor/operator keep /overview as home. The role is
                  // null while a switch is still loading, so fall back to where they are.
                  const staysOnEntry = role ? role === 'technician' : pathname.startsWith('/entry');
                  const to = staysOnEntry ? '/entry' : '/overview';
                  if (onIdRoute) {
                    // Leave a pond/cycle/job page first: switching farms under its id would
                    // refetch that id against the new farm and 404.
                    void navigate({ to }).then(() => setSelectedFarmId(farm.id));
                  } else {
                    setSelectedFarmId(farm.id);
                    void navigate({ to });
                  }
                }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 hover:bg-gray-50',
                  itemClass,
                  farm.id === farmId ? 'text-primary-600 font-medium' : 'text-gray-700'
                )}
              >
                <span className="truncate">{farm.name}</span>
                {farm.id === farmId && <Check className="w-4 h-4 shrink-0 text-primary-600" />}
              </button>
            ))}
            {isOperator && !drawer && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setIsCreateFarmOpen(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New Farm</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // One modal outside both branches: it must not remount (and lose typed input) when
  // the list arrives or after the first farm is created.
  return (
    <>
      {control}
      {!drawer && (
        <FarmCreateModal open={isCreateFarmOpen} onClose={() => setIsCreateFarmOpen(false)} />
      )}
    </>
  );
}
